import { initialFilterState } from '@/app/(main)/(pages)/busca/[slug]/_consts/search-filter.consts';
import { sanitizeDateFilterValues } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-date.util';
import { SearchFilterParamKey } from '@/app/(main)/(pages)/busca/[slug]/_consts/search-filter-params.const';
import { FilterState } from '@/app/(main)/(pages)/busca/[slug]/_types/filter-state.type';
import { SearchFilterAgeGroupType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-age-group.type';
import { SearchFilterDayTimeType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-day-time.type';
import { SearchFilterProfessionalGenderType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-professional-gender.type';
import { SearchSortType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-sort.type';

type GetParam = (key: string) => string | null;

function parseEnum<T extends Record<string, string>>(
  value: string | null,
  enumObject: T,
  fallback: T[keyof T],
): T[keyof T] {
  const normalized = value?.toLowerCase();
  const match = Object.values(enumObject).find((option) => option.toLowerCase() === normalized);

  return (match as T[keyof T] | undefined) ?? fallback;
}

function parseBoolean(value: string | null): boolean {
  return value?.toLowerCase() === 'true';
}

function parseDistance(value: string | null): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : initialFilterState.distance;
}

export function parseFiltersFromParams(getParam: GetParam): FilterState {
  const { minDate, maxDate } = sanitizeDateFilterValues({
    minDate: getParam(SearchFilterParamKey.minDate) ?? undefined,
    maxDate: getParam(SearchFilterParamKey.maxDate) ?? undefined,
  });

  return {
    orderBy: parseEnum(getParam(SearchFilterParamKey.orderBy), SearchSortType, initialFilterState.orderBy),
    dayTime: parseEnum(getParam(SearchFilterParamKey.dayTime), SearchFilterDayTimeType, initialFilterState.dayTime),
    professionalGender: parseEnum(
      getParam(SearchFilterParamKey.professionalGender),
      SearchFilterProfessionalGenderType,
      initialFilterState.professionalGender,
    ),
    ageGroup: parseEnum(getParam(SearchFilterParamKey.ageGroup), SearchFilterAgeGroupType, initialFilterState.ageGroup),
    distance: parseDistance(getParam(SearchFilterParamKey.distance)),
    minDate,
    maxDate,
    followUp: parseBoolean(getParam(SearchFilterParamKey.followUp)),
    onlyScheduled: parseBoolean(getParam(SearchFilterParamKey.onlyScheduled)),
  };
}

export function areFiltersEqual(filters: FilterState, other: FilterState): boolean {
  const params = filtersToParamValues(filters);
  const otherParams = filtersToParamValues(other);

  return Object.keys(params).every((key) => params[key] === otherParams[key]);
}

export function filtersToParamValues(filters: FilterState): Record<string, string | null> {
  const toParam = <K extends keyof FilterState>(key: K): string | null =>
    filters[key] === initialFilterState[key] || filters[key] === undefined ? null : String(filters[key]);

  return {
    [SearchFilterParamKey.orderBy]: toParam('orderBy'),
    [SearchFilterParamKey.dayTime]: toParam('dayTime'),
    [SearchFilterParamKey.professionalGender]: toParam('professionalGender'),
    [SearchFilterParamKey.ageGroup]: toParam('ageGroup'),
    [SearchFilterParamKey.distance]: toParam('distance'),
    [SearchFilterParamKey.minDate]: toParam('minDate'),
    [SearchFilterParamKey.maxDate]: toParam('maxDate'),
    [SearchFilterParamKey.followUp]: toParam('followUp'),
    [SearchFilterParamKey.onlyScheduled]: toParam('onlyScheduled'),
  };
}
